/**
 * Default pricing configuration
 * Contains base prices for all component types across all categories
 * Used as fallback when custom prices not provided
 *
 * Categories:
 * - doors: Door types with optional subtypes (frames, materials)
 * - materials: Boards, edge banding (normal/diff/bevel)
 * - component: Hardware (hinges, connectors, rails)
 * - accessory: Decorative items (LEDs, handles, mirrors)
 * - labor: Labor costs (design, CNC, assembly, painting)
 * - special: Special items (equipment, countertops, curve blocks)
 *
 * Structure: Can be simple values (e.g., 25000) or nested objects
 * Example: edgeBanding: { normal: 2800, diff: 5800, bevel: 6800 }
 */

export const DEFAULT_PRICES = {
  doors: {
    acrylic: null, // tính theo giá phôi acrylic
    normal: {
      dulux: 250000, // giá cửa thường sơn dulux /m2
    }, //
    alum: null, // tính theo phôi nhôm
    glassInside: null, // tính theo loại kính bên trong
    noGrainPano: {
      dulux: 450000, // giá pano không vân sơn dulux /m2
      melamine: 300000, // giá pano không vân sơn melamine /m2
    },
    grainPano: {
      melamine: 400000, // giá pano có vân sơn melamine /m2
    },
    louver: {
      // dulux: 400000, // giá cửa lá sách sơn dulux /m2
      // melamine: 300000, // giá cửa lá sách sơn melamine /m2
      dulux: 800000, // giá cửa lá sách sơn dulux /m2
      melamine: 700000, // giá cửa lá sách sơn melamine /m2
    },
    milled: {
      dulux: 400000, // giá cửa phay sơn dulux /m2
      melamine: 300000, // giá cửa phay sơn melamine /m2
    },
    drilled: {
      // giống giá cửa phay
      dulux: 400000, // giá cửa đục lỗ sơn dulux /m2
      melamine: 300000, // giá cửa đục lỗ sơn melamine /m2
    },
  },
  materials: {
    boards: null,
    acrylicCabinetTrims: null, // giá theo acrylic
    edgeBanding: {
      normal: 2800, // dán cạnh thường /m
      diff: 5800, // dán cạnh 34 mm /m
      bevel: 6800, // dán cạnh vát 45 độ /m
    },
  },
  component: {
    hinges: 25000, // giá bản lề thường /cái
    hinges135: 30000, // giá bản lề 135 độ /cái
    connectors: 700, // giá chốt cam /cái
    rails: 40000, // giá ray /cái
    liftSupport: 20000,
  },
  accessory: {
    handles: null, // giá tay nắm tính theo loại tay nắm
    leds: 60000, // giá đèn led /m
    ledsDecor: 60000, // giá đèn led trang trí /m
    lightSwitches: 80000, // giá công tắc đèn /cái
    ledDrivers: 100000, // giá nguồn đèn led /cái
    clothesHangers: 30000, // giá móc treo quần áo /m
    mirrors: 1200000, // giá gương /m2
    legs: null, // giá chân tủ tính theo loại chân
    standardLock: 120000, // giá ổ khóa thường /cái
  },
  labor: {
    design: 20000, // giá thiết kế /m2
    cnc: 40000, // giá cắt CNC /m2
    edgeBanding: {
      normal: 2500, // giá dán cạnh thường /m
      diff: 15000, // giá dán cạnh 34 mm /m
      bevel: 15000, // giá dán cạnh vát 45 độ /m
    },
    packing: 30000, // giá đóng gói /m2
    painting: 250000, // giá sơn /m2
    assembly: {
      wardrobe: 350000, // giá lắp ráp tủ quần áo /m2
      totl: 350000, // giá lắp ráp tủ ốp tủ lạnh /m2
      dry: 350000, // giá lắp ráp tủ khô /m2
      kitchenLower: 350000, // giá lắp ráp tủ bếp dưới /m
      kitchenUpper: 350000, // giá lắp ráp tủ bếp trên /m
      kitchenCeiling: 500000, // giá lắp ráp tủ bếp đụng trần /m
      shoeCabinet: 350000, // giá lắp ráp tủ giày /m2
      decorative: 500000, // giá lắp ráp tủ trang trí /m2
      tv: 350000, // giá lắp ráp kệ tivi /m
      bed: 400000, // giá lắp ráp giường /m2
      bedDrawer: 90000, // giá lắp ngăn kéo giường /cái
      desk: 300000, // giá lắp ráp bàn /m
      vanity: 300000, // giá lắp ráp bàn trang điểm /m
      shelf: 300000, // giá lắp ráp kệ /m
      shoeBench: 400000, // giá lắp ráp đôn ngồi /m2
      wallPanelMelamine: 400000, // giá lắp ráp vách ngăn melamine /m2
      slatWallPartition: 700000, // giá lắp ráp vách ngăn /m2
      wallPanelMirror: 500000, // giá lắp ráp vách ngăn gương /m2
      led: 100000, // giá lắp đèn led /m
      bedLower: 400000, // giá lắp giường bục /m2
    },
  },
  special: {
    equipments: null, // giá thiết bị bếp tính theo loại thiết bị
    countertop: null, // giá mặt bàn tính theo loại vật liệu
    backsplash: null, // giá ốp tường bếp tính theo loại vật liệu
    curveBlocks: {
      'rank1.AC.M': 1000000, // giá block cong loại AC, h từ 300-600 mm, m2
      'rank1.AC.L': 1500000, // giá block cong loại AC, h từ 300-600 mm, l2
      'rank1.AC.Plastic': 1200000, // giá block cong loại AC, h từ 300-600 mm, nhựa
      'rank1.KES.M': 800000, // giá block cong loại KES, h từ 300-600 mm, m2
      'rank1.KES.L': 1200000, // giá block cong loại KES, h từ 300-600 mm, l2
      'rank1.ACC.Plywood': 1200000, // giá block cong loại ACC, h từ 300-600 mm
      'rank2.AC.M': 2000000, // giá block cong loại AC, h từ 600-1000 mm, m2
      'rank2.AC.L': 2500000, // giá block cong loại AC, h từ 600-1000 mm, l2
      'rank2.AC.Plastic': 2500000, // giá block cong loại AC, h từ 600-1000 mm, nhựa
      'rank2.KES.M': 1500000, // giá block cong loại KES, h từ 600-1000 mm, m2
      'rank2.KES.L': 1800000, // giá block cong loại KES, h từ 600-1000 mm, l2
      'rank2.ACC.Plywood': 2500000, // giá block cong loại ACC, h từ 600-1000 mm
      'rank3.AC.M': 3000000, // giá block cong loại AC, h từ 1000-2000 mm, m2
      'rank3.AC.L': 3500000, // giá block cong loại AC, h từ 1000-2000 mm, l2
      'rank3.AC.Plastic': 3500000, // giá block cong loại AC, h từ 1000-2000 mm, nhựa
      'rank3.KES.M': 2800000, // giá block cong loại KES, h từ 1000-2000 mm, m2
      'rank3.KES.L': 3000000, // giá block cong loại KES, h từ 1000-2000 mm, l2
      'rank3.ACC.Plywood': 4000000, // giá block cong loại ACC, h từ 1000-2000 mm
    },
  },
  transport: {
    importDuty: null,
    shippingFee: 500, // đơn vị $
    groundTripFee: 300, // đơn vị $
  },
  variables: {
    exchangeRate: 26200, // đơn vị VNĐ
    gst: 0.1,
  },
};

/**
 * Default profit margins for all component types
 * Profit margin: 0.0-1.0 range (e.g., 0.4 = 40% markup on cost)
 * Used in formula: salePrice = costPrice / (1 - profitMargin) * 1.08
 * Structure mirrors DEFAULT_PRICES with same categories and subtypes
 */

export const DEFAULT_PROFITS = {
  doors: {
    acrylic: 0.2, // tính theo giá phôi acrylic
    normal: {
      dulux: 0.4, // giá cửa thường sơn dulux /m2
    }, //
    alum: 0.3, // tính theo phôi nhôm
    glassInside: 0.4, // tính theo loại kính bên trong
    noGrainPano: {
      dulux: 0.4, // giá pano không vân sơn dulux /m2
      melamine: 0.4, // giá pano không vân sơn melamine /m2
    },
    grainPano: {
      melamine: 0.4, // giá pano có vân sơn melamine /m2
    },
    louver: {
      // dulux: 400000, // giá cửa lá sách sơn dulux /m2
      // melamine: 300000, // giá cửa lá sách sơn melamine /m2
      dulux: 0.4, // giá cửa lá sách sơn dulux /m2
      melamine: 0.4, // giá cửa lá sách sơn melamine /m2
    },
    milled: {
      dulux: 0.4, // giá cửa phay sơn dulux /m2
      melamine: 0.4, // giá cửa phay sơn melamine /m2
    },
    drilled: {
      // giống giá cửa phay
      dulux: 0.4, // giá cửa đục lỗ sơn dulux /m2
      melamine: 0.4, // giá cửa đục lỗ sơn melamine /m2
    },
  },
  materials: {
    boards: 0.4,
    acrylicCabinetTrims: 0.2, // giá theo acrylic

    edgeBanding: {
      normal: 0.4, // dán cạnh thường /m
      diff: 0.4, // dán cạnh 34 mm /m
      bevel: 0.4, // dán cạnh vát 45 độ /m
    },
  },
  component: {
    hinges: 0.4, // giá bản lề thường /cái
    hinges135: 0.4, // giá bản lề 135 độ /cái
    connectors: 0.4, // giá chốt cam /cái
    rails: 0.4,
    liftSupport: 0.4,
  },
  accessory: {
    handles: 0.4, // giá tay nắm tính theo loại tay nắm
    leds: 0.2, // giá đèn led /m
    ledsDecor: 0.4, // giá đèn led trang trí /m
    lightSwitches: 0.4, // giá công tắc đèn /cái
    ledDrivers: 0.4, // giá nguồn đèn led /cái
    clothesHangers: 0.4, // giá móc treo quần áo /m
    mirrors: 0.4, // giá gương /m2
    legs: 0.4, // giá chân tủ tính theo loại chân
    standardLock: 0.4, // giá ổ khóa thường /cái
  },
  labor: {
    design: 0.4, // giá thiết kế /m2
    cnc: 0.4, // giá cắt CNC /m2
    edgeBanding: {
      normal: 0.4, // giá dán cạnh thường /m
      // normal: 0.4, // giá dán cạnh thường /m
      diff: 0.4, // giá dán cạnh 34 mm /m
      bevel: 0.4, // giá dán cạnh vát 45 độ /m
    },
    packing: 0.4, // giá đóng gói /m2
    painting: 0.4, // giá sơn /m2
    assembly: {
      wardrobe: 0.4, // giá lắp ráp tủ quần áo /m2
      totl: 0.4, // giá lắp ráp tủ ốp tủ lạnh /m2
      dry: 0.4, // giá lắp ráp tủ khô /m2
      kitchenLower: 0.4, // giá lắp ráp tủ bếp dưới /m
      kitchenUpper: 0.4, // giá lắp ráp tủ bếp trên /m
      kitchenCeiling: 0.4, // giá lắp ráp tủ bếp đụng trần /m
      shoeCabinet: 0.4, // giá lắp ráp tủ giày /m2
      decorative: 0.4, // giá lắp ráp tủ trang trí /m2
      tv: 0.4, // giá lắp ráp kệ tivi /m
      bed: 0.4, // giá lắp ráp giường /cái
      bedDrawer: 0.4, // giá lắp ngăn kéo giường /cái
      desk: 0.4, // giá lắp ráp bàn /m
      vanity: 0.4, // giá lắp ráp bàn trang điểm /m
      shelf: 0.4, // giá lắp ráp kệ /m
      shoeBench: 0.4, // giá lắp ráp đôn ngồi /m2
      wallPanelMelamine: 0.4, // giá lắp ráp vách ngăn melamine /m2
      slatWallPartition: 0.4, // giá lắp ráp vách ngăn /m2
      wallPanelMirror: 0.4, // giá lắp ráp vách ngăn gương /m2
      led: 0.4, // giá lắp đèn led /m
      bedLower: 0.4, // giá lắp giường bục /m2
    },
  },
  special: {
    equipments: 1, // giá thiết bị bếp tính theo loại thiết bị
    countertop: 1, // giá mặt bàn tính theo loại vật liệu
    backsplash: 1, // giá ốp tường bếp tính theo loại vật liệu
    curveBlocks: {
      'rank1.AC.M': 1, // giá block cong loại AC, h từ 300-600 mm, m2
      'rank1.AC.L': 1, // giá block cong loại AC, h từ 300-600 mm, l2
      'rank1.AC.Plastic': 1, // giá block cong loại AC, h từ 300-600 mm, nhựa
      'rank1.KES.M': 1, // giá block cong loại KES, h từ 300-600 mm, m2
      'rank1.KES.L': 1, // giá block cong loại KES, h từ 300-600 mm, l2
      'rank1.ACC.Plywood': 1, // giá block cong loại ACC, h từ 300-600 mm
      'rank2.AC.M': 1, // giá block cong loại AC, h từ 600-1000 mm, m2
      'rank2.AC.L': 1, // giá block cong loại AC, h từ 600-1000 mm, l2
      'rank2.AC.Plastic': 1, // giá block cong loại AC, h từ 600-1000 mm, nhựa
      'rank2.KES.M': 1, // giá block cong loại KES, h từ 600-1000 mm, m2
      'rank2.KES.L': 1, // giá block cong loại KES, h từ 600-1000 mm, l2
      'rank2.ACC.Plywood': 1, // giá block cong loại ACC, h từ 600-1000 mm
      'rank3.AC.M': 1, // giá block cong loại AC, h từ 1000-2000 mm, m2
      'rank3.AC.L': 1, // giá block cong loại AC, h từ 1000-2000 mm, l2
      'rank3.AC.Plastic': 1, // giá block cong loại AC, h từ 1000-2000 mm, nhựa
      'rank3.KES.M': 1, // giá block cong loại KES, h từ 1000-2000 mm, m2
      'rank3.KES.L': 1, // giá block cong loại KES, h từ 1000-2000 mm, l2
      'rank3.ACC.Plywood': 1, // giá block cong loại ACC, h từ 1000-2000 mm
    },
  },
  transport: {
    importDuty: 0.25,
    shippingFee: 0.2,
    groundTripFee: 0.2,
  },
};

export default {
  DEFAULT_PRICES,
  DEFAULT_PROFITS,
};
